<!-- TODO rename component to Pill or something, it's not a Tag -->
<template>
  <div
    data-component="am3-tag"
    class="inline-flex items-center select-none text-xs font-medium whitespace-nowrap h-6 gap-1 outline outline-surface-light outline-1"
    :class="[
      typeClasses[type],
      closable ? 'pl-2' : 'px-2',
      rounded === true ? 'rounded-full' : 'rounded-md',
    ]"
    :data-test-id="generateDataTestId(dataTestId, 'tag')"
  >
    <slot name="before" />
    <svg
      v-if="dot"
      xmlns="http://www.w3.org/2000/svg"
      class="size-[6px] mr-1"
      viewBox="0 0 6 6"
    >
      <circle
        cx="3"
        cy="3"
        r="3"
        fill="currentColor"
      />
    </svg>
    <span
      v-if="label"
      v-tooltip.bottom="{
        value: label.length >= 128 && label.length > 0 || truncate ? getTruncatedString(label) : null,
        hideDelay: 125,
        showDelay: 1,
      }"
      :class=" truncate ? 'max-w-24' : ' max-w-screen-xs' "
      class="cursor-default truncate text-ellipsis inline-block"
    >
      {{ label }}
    </span>
    <slot name="after" />
    <button
      v-if="closable"
      class="mx-px px-1 size-5 my-auto rounded-full outline-transparent outline-1 outline focus:outline-brand cursor-pointer"
      :class="[
        type === 'surface' ? 'hover:bg-stone-100' : 'hover:bg-stone-50',
        type === 'outline' ? 'outline-offset-[-2px] outline-width-px' : '',
      ]"
      :data-test-id="generateDataTestId(dataTestId, 'tag-close')"
      @click.stop="handleClose"
    >
      <span class="sr-only">Close Tag - {{ label }}</span>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        class="size-3"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M6 18L18 6M6 6l12 12"
        />
      </svg>
    </button>
  </div>
</template>

<script setup lang="ts">
import { generateDataTestId } from '@/components/am3-generic/core/dataTestId'
import { getTruncatedString } from '@/utils/string'

export type TagType = 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'brand' | 'surface' | 'outline' | 'completed' | 'refund' | 'cancelled' | 'live' | 'scheduled' | 'subscribed' | 'unsubscribed' | 'upcoming' | 'past' | 'gradient'

// Interface for tag data objects (used in selects, forms, etc.)
export interface Tag {
  oid: number
  name: string
  value?: number | string
  label?: string
}

// Props interface for the AM3Tag component
interface Props {
  label: string
  type?: TagType
  dataTestId?: string
  closable?: boolean
  truncate?: boolean
  dot?: boolean
  rounded?: boolean
}

withDefaults(defineProps<Props>(), {
  type: 'brand',
  dataTestId: 'tag',
  closable: false,
  truncate: false,
  dot: false,
  rounded: true,
})

const emit = defineEmits(['close'])

const typeClasses = {
  default: 'bg-gray-100 text-gray-800',
  brand: 'bg-surfaces-informative text-font-brand ',
  primary: 'bg-gray-100 text-gray-800',
  secondary: 'bg-gray-100 text-secondary',
  success: 'bg-green-50 text-green-700',
  warning: 'bg-yellow-50 text-yellow-700',
  danger: 'bg-red-50 text-red-700',
  surface: 'bg-surfaces-neutral text-primary',
  outline: 'border border-brand border-solid text-brand bg-transparent',
  completed: 'bg-brand-light text-brand',
  refund: 'bg-surfaces-danger text-danger',
  cancelled: 'bg-surface text-primary',
  live: 'bg-surfaces-success text-green-700',
  scheduled: 'bg-surfaces-warning text-orange-700',
  subscribed: 'bg-transparent text-green-700',
  unsubscribed: 'bg-transparent text-danger',
  upcoming: 'bg-green-50 text-green-700',
  past: 'bg-surface text-primary',
  gradient: 'bg-gradient-to-r from-[#B758F6] to-[#DE5BEC] text-white',
}

const handleClose = () => {
  emit('close')
}
</script>
